import { useCallback, useState } from 'react';
import { noop } from '@cbhq/cds-utils';
import { Banner } from '@cbhq/cds-web/banner/Banner';
import { Button } from '@cbhq/cds-web/buttons';
import { Select, SelectOption } from '@cbhq/cds-web/controls';
import { VStack } from '@cbhq/cds-web/layout';
import { Modal, ModalBody, ModalFooter, ModalHeader, ModalProps } from '@cbhq/cds-web/overlays';
import { useToast } from '@cbhq/cds-web/overlays/useToast';
import { TextHeadline } from '@cbhq/cds-web/typography';

import { useAppState } from ':cloud/contexts/AppStateContext';
import {
  type ExpiresTime,
  expiresTimes,
  useRotateToken,
} from ':cloud/queries/ApiKeyQueries/useRotateToken';
import { useGetUser } from ':cloud/queries/UserQueries/useGetUser';

type TokenRotateModalProps = Partial<ModalProps> & {
  onSuccess?: (clientId: string) => void;
  tokenId: string;
};

export function TokenRotateModal(props: TokenRotateModalProps): JSX.Element {
  const { onRequestClose, onSuccess, tokenId } = props;
  const { activeOrg } = useGetUser();
  const { selectedProject } = useAppState();
  const toast = useToast();
  const [expiresTime, setExpiresTime] = useState<ExpiresTime | undefined>();

  const { mutate: rotateToken, isLoading } = useRotateToken(
    activeOrg?.organizationId,
    selectedProject?.id,
    (rotatedTokenId) => {
      toast.show('Client API Key successfully rotated.', {
        variant: 'positive',
      });
      onSuccess?.(rotatedTokenId);
      onRequestClose?.();
    },
  );

  const handleSubmit = useCallback(() => {
    if (expiresTime !== undefined) {
      rotateToken({ tokenId, expiresTime });
    }
  }, [rotateToken, tokenId, expiresTime]);

  if (!tokenId) {
    throw new Error('Client API Key ID not available');
  }

  return (
    <Modal onRequestClose={onRequestClose || noop} visible {...props}>
      <ModalHeader title="Rotate Client API Key" />
      <ModalBody>
        <VStack gap={4}>
          <Banner title="Warning" variant="warning" startIcon="warning">
            This key will no longer be functional after expiration. Please update all instances of
            this key, including in your RPC endpoint, to avoid any service disruptions.
          </Banner>
          <TextHeadline as="p">Expiration of Current Key</TextHeadline>
          <Select
            value={expiresTime}
            placeholder="Select an expiration time"
            onChange={(nextExpiresTime: string) => setExpiresTime(nextExpiresTime as ExpiresTime)}
          >
            {expiresTimes.map((option) => (
              <SelectOption value={option} key={option} title={option} description="" />
            ))}
          </Select>
        </VStack>
      </ModalBody>
      <ModalFooter
        primaryAction={
          <Button
            type="submit"
            onPress={handleSubmit}
            loading={isLoading}
            disabled={expiresTime === undefined}
          >
            Confirm Rotation
          </Button>
        }
        secondaryAction={
          <Button variant="secondary" onPress={onRequestClose}>
            Cancel
          </Button>
        }
      />
    </Modal>
  );
}
